import React, { useState, useEffect } from "react";
import axios from "../utils/axiosConfig";
import { Box, Button, TextField, Typography, Select, MenuItem } from "@mui/material";
import { DataGrid, GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import YearPicker from "../components/YearPicker";

import { handleApiError } from "../utils/apiErrorHandler";

interface Athlete {
  _id: string;
  firstname: string;
  surname: string;
  age: number;
  gender: string;
}

const AthletesManagementPage: React.FC = () => {
  const [athletes, setAthletes] = useState<Athlete[]>([]);
  const [editingAthlete, setEditingAthlete] = useState<Athlete | null>(null);
  const [newAthlete, setNewAthlete] = useState<Athlete>({ _id: "", firstname: "", surname: "", age: 1900, gender: "m" });
  const apiRoute = "/api/athletes"; // API-Endpunkt für Athleten
  const [editedRows, setEditedRows] = useState<{ [key: string]: boolean }>({});

  // Athleten laden
  useEffect(() => {
    fetchAthletes();
  }, []);

  const fetchAthletes = async () => {
    try {
      const response = await axios.get(apiRoute);
      setAthletes(response.data);
    } catch (error) {
      handleApiError(error, "Fehler beim Laden der Athleten:");
    }
  };

  // Athlet hinzufügen
  const addAthlete = async () => {
    try {
      await axios.post(apiRoute, newAthlete);
      setNewAthlete({ _id: "", firstname: "", surname: "", age: 0, gender: "m" });
      fetchAthletes();
    } catch (error: any) {
      handleApiError(error, "Fehler beim Hinzufügen eines Athleten.");
    }
  };

  // Athlet löschen
  const deleteAthlete = async (athleteId: string) => {
    try {
      await axios.delete(`${apiRoute}/${athleteId}`);
      fetchAthletes();
    } catch (error: any) {
      handleApiError(error, "Fehler beim Löschen des Athleten.");
    }
  };

  // Änderungen speichern
  const saveRow = async (athlete: Athlete) => {
    try {
      await axios.put(`${apiRoute}/${athlete._id}`, athlete);
      setEditedRows((prev) => ({ ...prev, [athlete._id]: false })); // Markierung entfernen
      fetchAthletes();
    } catch (error: any) {
      handleApiError(error, "Fehler beim Speichern der Änderungen.");
    }
  };

  // Custom renderer for YearPicker in DataGrid
  const renderYearPicker = (params: GridRenderCellParams) => {
    return (
      <YearPicker
        value={params.value}
        onChange={(year) => {
          if (year) {
            // Update the row with the new year value
            const updatedRow = { ...params.row, birthYear: year };
            params.api.updateRows([updatedRow]);
            setEditedRows((prev) => ({ ...prev, [params.row._id]: true }));
          }
        }}
        required
        fullWidth={false}
        variant="standard"
        hideLabel={true}
        disableValidation={true}
      />
    );
  };

  // Spalten für die DataGrid-Tabelle
  const columns: GridColDef[] = [
    { field: "firstname", headerName: "Vorname", flex: 1, minWidth: 100, editable: true },
    { field: "surname", headerName: "Nachname", flex: 1, minWidth: 100, editable: true },
    { field: "age", 
      headerName: "Jahrgang", 
      flex: 0.8, 
      minWidth: 120, 
      renderCell: renderYearPicker, 
      editable: false // We handle editing via custom renderer
    } ,
    {
      field: "gender",
      headerName: "Geschlecht",
      flex: 0.5,
      minWidth: 100,
      renderCell: (params) => (params.value === "m" ? "Männlich" : "Weiblich"),
    },
    {
      field: "actions",
      headerName: "Aktionen",
      flex: 1,
      minWidth: 120,
      renderCell: (params) => (
        <Box>
          <Button 
            variant="contained" 
            color="success" 
            size="small" 
            onClick={() => saveRow(params.row)}
            disabled={!editedRows[params.row._id]} // Button nur aktiv, wenn etwas geändert wurde
          >
            Speichern
          </Button>
          <Button 
            variant="contained" 
            color="error" 
            size="small" 
            onClick={() => deleteAthlete(params.row._id)}
            sx={{ ml: 1 }}
          >
            Löschen
          </Button>
        </Box>
      ),
    },
  ];

  return (
    <>
      <Typography variant="h5" gutterBottom>
        Athletenverwaltung
      </Typography>

      {/* Eingabefelder für neue Athleten */}
      <Box display="flex" gap={2} sx={{ mb: 2 }}>
        <TextField label="Vorname" value={newAthlete.firstname} onChange={(e) => setNewAthlete({ ...newAthlete, firstname: e.target.value })} />
        <TextField label="Nachname" value={newAthlete.surname} onChange={(e) => setNewAthlete({ ...newAthlete, surname: e.target.value })} />
        <Box sx={{ minWidth: 200 }}>
          <YearPicker 
            label="Jahrgang"
            value={newAthlete.age || null}
            onChange={(year) => setNewAthlete({ ...newAthlete, age: year as number })}
            required
            variant="outlined"
            hideLabel={true}
            disableValidation={true}
          />
        </Box>
        <Select value={newAthlete.gender} onChange={(e) => setNewAthlete({ ...newAthlete, gender: e.target.value as string })}>
          <MenuItem value="m">Männlich</MenuItem>
          <MenuItem value="w">Weiblich</MenuItem>
        </Select>
        <Button variant="contained" color="primary" onClick={addAthlete}>
          Hinzufügen
        </Button>
      </Box>

      {/* MUI DataGrid für Athleten */}
      <Box sx={{ height: "auto", width: "100%", mt: 2 }}>
        <DataGrid
          rows={athletes}
          columns={columns}
          getRowId={(row) => row._id}
          pageSizeOptions={[10, 25, 50]}
          initialState={{
            pagination: {
              paginationModel: { pageSize: 10 },
            },
          }}
          processRowUpdate={(updatedRow) => {
            setEditedRows((prev) => ({ ...prev, [updatedRow._id]: true })); // Zeile als geändert markieren
            return updatedRow;
          }}
          sx={{
            height: "auto",
            minHeight: 400, // Mindesthöhe für gute Lesbarkeit
            maxHeight: "70vh", // Verhindert, dass die Tabelle zu groß wird
            "& .MuiDataGrid-row": {
              transition: "background-color 0.3s",
            },
            "& .MuiDataGrid-row.edited": {
              backgroundColor: "#fff3cd", // Hellgelb für geänderte Zeilen
            },
          }}
          getRowClassName={(params) => (editedRows[params.row._id] ? "edited" : "")}
        />
      </Box>
    </>
  );
};

export default AthletesManagementPage;